import { Flex, Select, Space, Spin, Typography } from "antd";
import type { SelectProps } from "antd";
import { Button, Popconfirm } from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { VoiceLanguagesResponse } from "types/";

type VoiceCloneLanguageSelectProps = Omit<
  SelectProps,
  "options" | "onChange"
> & {
  languages?: VoiceLanguagesResponse;
  onChange?: (value: string | undefined) => void;
  hasSamples?: boolean;
};

export const VoiceCloneLanguageSelect = ({
  value,
  onChange,
  languages,
  placeholder,
  allowClear = false,
  style = { width: "100%" },
  disabled = false,
  size = "middle",
  loading,
  hasSamples = false,
  ...restProps
}: VoiceCloneLanguageSelectProps) => {
  const t = useTranslate();
  const selectedLanguage =
    typeof value === "string" && value ? languages?.[value] : undefined;

  if (typeof value === "string" && value) {
    const label = selectedLanguage
      ? `${selectedLanguage.native_name} (${selectedLanguage.name})`
      : value;
    const clear = () => onChange?.(undefined);
    return (
      <Flex align="center" justify="space-between" gap={10} wrap="wrap">
        <Space align="center">
          <Typography.Text>
            {t("media.components.VoiceProfileCreateForm.selectedLanguageLabel")}
          </Typography.Text>
          <Typography.Text strong>{label}</Typography.Text>
        </Space>
        <Popconfirm
          disabled={!hasSamples}
          title={t(
            "media.components.VoiceProfileCreateForm.changeLanguageConfirmTitle"
          )}
          description={t(
            "media.components.VoiceProfileCreateForm.changeLanguageConfirmDescription"
          )}
          confirmText={t(
            "media.components.VoiceProfileCreateForm.changeLanguageConfirmOk"
          )}
          cancelText={t(
            "media.components.VoiceProfileCreateForm.changeLanguageConfirmCancel"
          )}
          danger
          onConfirm={clear}
          styles={{
            body: {
              width: 400,
            },
          }}
        >
          <Button
            variant="tertiary"
            size="s"
            style={{ padding: 0, boxShadow: "none" }}
            onClick={hasSamples ? undefined : clear}
          >
            {t("media.components.VoiceProfileCreateForm.changeLanguage")}
          </Button>
        </Popconfirm>
      </Flex>
    );
  }

  const options = Object.entries(languages ?? {}).map(([code, language]) => ({
    value: code,
    label: `${language.native_name} (${language.name})`,
  }));

  return (
    <Select<string>
      value={value}
      onChange={(selectedValue) => onChange?.(selectedValue)}
      placeholder={placeholder}
      allowClear={allowClear}
      style={style}
      disabled={disabled}
      size={size}
      options={options}
      optionFilterProp="label"
      showSearch
      notFoundContent={
        loading ? (
          <Spin size="small" style={{ display: "block", margin: "8px auto" }} />
        ) : undefined
      }
      {...restProps}
    />
  );
};
